import React from 'react';
import styles from './LoginPage.module.scss';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { withTranslation } from 'react-i18next';
import useSWRMutation from 'swr/mutation';
import axios from '../../store/middlewares/axios';
import { generateCodeVerifier, generateCodeChallenge } from 'utils/pkce';
import { getPasskey, isUserCancelledError, isWebAuthnAvailable } from 'utils/webauthn';
import {
    consumePostLoginRedirect,
    rememberPostLoginRedirect,
    sanitizeRedirectPath,
} from 'utils';
import * as actionTypes from '../../store/actionTypes';

import SparaButton from '../../components/SparaButton/SparaButton';
import LanguageSwitcher from '../../components/LanguageSwitcher/LanguageSwitcher';
import { notifyThemeAuthChanged } from '../../utils/theme';
import ThemeToggle from '../../components/ThemeToggle/ThemeToggle';
import { buildMockLoginResults, isApiMockEnabled } from 'api/mock';

const TAB_DEFS = [
    { id: 0, labelKey: 'login.tab_electronic_id' },
    { id: 1, labelKey: 'login.tab_audkenni_app' },
    { id: 2, labelKey: 'login.tab_email' },
    { id: 3, labelKey: 'login.tab_passkey' },
];

const EMAIL_RE = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

const POLL_INTERVAL_MS = 2000;
const POLL_TIMEOUT_MS = 95000;

async function emailFetcher(url, { arg }) {
    const res = await axios({ url, method: 'POST', data: arg });
    return res.data;
}

function isThrottled(err) {
    if (err?.response?.status === 429) return true;
    const msg = err?.response?.data?.message || '';
    return msg.toLowerCase().includes('too many attempts');
}

async function pollAudkenni(initialToken) {
    let currentToken = initialToken;
    const startTime = Date.now();
    let throttleBackoff = 8000;

    while (Date.now() - startTime < POLL_TIMEOUT_MS) {
        await new Promise((r) => setTimeout(r, POLL_INTERVAL_MS));

        let pollResult;
        try {
            pollResult = await axios({
                url: '/api/auth/audkenni/poll',
                method: 'POST',
                data: { token: currentToken },
                timeout: 10000,
            });
        } catch (err) {
            if (isThrottled(err)) {
                const retryAfter = err?.response?.headers?.['retry-after'];
                const wait = retryAfter ? parseInt(retryAfter, 10) * 1000 : throttleBackoff;
                await new Promise((r) => setTimeout(r, wait));
                throttleBackoff = Math.min(throttleBackoff * 2, 30000);
                continue;
            }
            throw err;
        }

        // Reset backoff on successful response
        throttleBackoff = 8000;
        currentToken = pollResult.data.token ?? currentToken;
        const { status } = pollResult.data;

        if (status === 'authenticated') return { status, token: currentToken };
        if (status === 'failed') {
            return {
                status,
                token: currentToken,
                message: pollResult.data.message || pollResult.data.error,
                failure_reason: pollResult.data.failure_reason,
            };
        }
    }

    return { status: 'timeout', token: currentToken };
}

function parseUserData(results) {
    return {
        access_token: results.access_token,
        token_type: results.token_type,
        roles: results.roles,
        permission: results.permission.map((v) => v.name),
        user: results.user[0],
        companyIds: results.user.map((v) => v.torgid_company_id),
        companies: results.user
            .filter((v) => v.torgid_company !== null)
            .map((v) => v.torgid_company),
        groupIds: results.user
            .filter((v) => v.torgid_special_company_id !== null)
            .map((v) => v.torgid_special_company_id),
        groupTypes: results.user
            .filter((v) => v.torgid_special_company_id !== null)
            .map((v) => (v.torgid_special_company ? v.torgid_special_company.type : '')),
        groups: results.user
            .filter(
                (v) => v.torgid_special_company !== null && v.torgid_special_company !== undefined
            )
            .map((v) => v.torgid_special_company),
    };
}

function LoginPage({ t }) {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const { authUser } = useSelector((state) => state.auth);

    const [fadingOut, setFadingOut] = React.useState(false);
    const [activeTab, setActiveTab] = React.useState(() => {
        const saved = localStorage.getItem('selectedLoginTab');
        return saved ? parseInt(saved, 10) : 0;
    });
    const [apiError, setApiError] = React.useState('');

    const panelRef = React.useRef(null);
    const viewportRef = React.useRef(null);

    // Phone (Rafræn skilríki) state
    const [phone, setPhone] = React.useState('');
    const [phoneError, setPhoneError] = React.useState('');
    const [phoneLoading, setPhoneLoading] = React.useState(false);
    const [phoneVcode, setPhoneVcode] = React.useState(null);

    // Kennitala (Auðkennisapp) state
    const [kennitala, setKennitala] = React.useState('');
    const [kennitalaError, setKennitalaError] = React.useState('');
    const [kennitalaLoading, setKennitalaLoading] = React.useState(false);
    const [kennitalaVcode, setKennitalaVcode] = React.useState(null);

    // Email state
    const [email, setEmail] = React.useState('');
    const [emailError, setEmailError] = React.useState('');
    const [password, setPassword] = React.useState('');
    const [passwordError, setPasswordError] = React.useState('');

    // Passkey state
    const [passkeyLoading, setPasskeyLoading] = React.useState(false);
    const passkeySupported = isWebAuthnAvailable();

    const location = useLocation();
    const { trigger: triggerEmail, isMutating: emailLoading } = useSWRMutation(
        '/api/auth/admin_login',
        emailFetcher
    );

    React.useEffect(() => {
        if (!authUser || (Array.isArray(authUser) && authUser.length === 0)) {
            sessionStorage.removeItem('justTransitioning');
        }
    }, []); // eslint-disable-line

    React.useEffect(() => {
        const params = new URLSearchParams(location.search);
        const redirect = sanitizeRedirectPath(params.get('redirect'));
        if (redirect) {
            rememberPostLoginRedirect(redirect);
        }
    }, [location.search]);

    React.useEffect(() => {
        document.title = `${t('login.title')} | Spara`;
    }, [t]);

    React.useEffect(() => {
        if (authUser && !Array.isArray(authUser) && sessionStorage.getItem('justTransitioning')) {
            setFadingOut(true);
            const timer = setTimeout(() => {
                sessionStorage.removeItem('justTransitioning');
                sessionStorage.setItem('justLoggedIn', '1');
                navigate(consumePostLoginRedirect(location.search) || '/');
            }, 400);
            return () => clearTimeout(timer);
        }
    }, [authUser, navigate, location.search]);

    React.useEffect(() => {
        const panel = panelRef.current;
        if (panel && viewportRef.current) {
            viewportRef.current.style.height = `${panel.offsetHeight}px`;
        }
    });

    const saveUserAndRedirect = (data) => {
        localStorage.setItem('user', JSON.stringify(data));
        notifyThemeAuthChanged();
        sessionStorage.setItem('justTransitioning', '1');
        dispatch({ type: actionTypes.LOGIN_SUCCESS, data });
    };

    // Deterministic mock session for Maestro web (focus/inputText is flaky on Chromium).
    // Only active when REACT_APP_API_MOCK=1 — never in production builds.
    React.useEffect(() => {
        if (!isApiMockEnabled()) return;
        const params = new URLSearchParams(location.search);
        if (params.get('e2e_login') !== '1') return;
        if (!localStorage.getItem('user')) {
            saveUserAndRedirect(parseUserData(buildMockLoginResults()));
        }
        // Prefer react-router navigate — hard reload can leave Maestro web with a 1px viewport.
        sessionStorage.removeItem('justTransitioning');
        sessionStorage.setItem('justLoggedIn', '1');
        navigate(consumePostLoginRedirect(location.search) || '/', { replace: true });
    }, [location.search, dispatch, navigate]);

    const handleTabChange = (tabId) => {
        setActiveTab(tabId);
        localStorage.setItem('selectedLoginTab', tabId.toString());
        setApiError('');
        setPhoneError('');
        setKennitalaError('');
        setEmailError('');
        setPasswordError('');
    };

    const handlePhoneSubmit = async () => {
        const trimmed = phone.trim();
        if (!/^\d{7}$/.test(trimmed)) {
            setPhoneError(t('login.phone_must_be_7_digits'));
            return;
        }
        setPhoneError('');
        setApiError('');
        setPhoneLoading(true);
        setPhoneVcode(null);

        const codeVerifier = generateCodeVerifier();
        sessionStorage.setItem('code_verifier', codeVerifier);
        const codeChallenge = await generateCodeChallenge(codeVerifier);

        try {
            const res = await axios({
                url: '/api/auth/audkenni',
                method: 'POST',
                data: { type: 'admin', phone: '+354' + trimmed, code_challenge: codeChallenge },
            });
            setPhoneVcode(res.data.vcode);

            const poll = await pollAudkenni(res.data.token);

            if (poll.status === 'timeout') {
                setApiError(t('login.timeout_error'));
                return;
            }
            if (poll.status === 'failed') {
                setApiError(poll.message || t('login.auth_error'));
                return;
            }

            const userResult = await axios({
                url: '/api/auth/audkenni/user',
                method: 'POST',
                data: { token: poll.token, code_verifier: codeVerifier },
            });

            if (userResult.data.message === 'Successfully login.' && userResult.data.code === 200) {
                saveUserAndRedirect(parseUserData(userResult.data.results));
            }
        } catch (error) {
            const res = error?.response?.data;
            if (res?.code === 404) {
                setApiError(t('login.no_admin_with_phone'));
            } else {
                const msg = res?.results ? res.results[Object.keys(res.results)[0]] : '';
                setApiError(
                    res?.message || res?.error || t('login.error_occurred') + (msg ? '. ' + msg : '')
                );
            }
        } finally {
            setPhoneLoading(false);
            setPhoneVcode(null);
        }
    };

    const handleKennitalaSubmit = async () => {
        const trimmed = kennitala.trim().replace(/-/g, '');
        if (!/^\d{10}$/.test(trimmed)) {
            setKennitalaError(t('login.kennitala_must_be_10_digits'));
            return;
        }
        setKennitalaError('');
        setApiError('');
        setKennitalaLoading(true);
        setKennitalaVcode(null);

        const codeVerifier = generateCodeVerifier();
        sessionStorage.setItem('code_verifier', codeVerifier);
        const codeChallenge = await generateCodeChallenge(codeVerifier);

        try {
            const res = await axios({
                url: '/api/auth/audkenni',
                method: 'POST',
                data: { type: 'admin', kennitala: trimmed, code_challenge: codeChallenge },
            });
            setKennitalaVcode(res.data.vcode);

            const poll = await pollAudkenni(res.data.token);

            if (poll.status === 'timeout') {
                setApiError(t('login.timeout_error'));
                return;
            }
            if (poll.status === 'failed') {
                setApiError(poll.message || t('login.auth_error'));
                return;
            }

            const userResult = await axios({
                url: '/api/auth/audkenni/user',
                method: 'POST',
                data: { token: poll.token, code_verifier: codeVerifier },
            });

            if (userResult.data.message === 'Successfully login.' && userResult.data.code === 200) {
                saveUserAndRedirect(parseUserData(userResult.data.results));
            }
        } catch (error) {
            const res = error?.response?.data;
            if (res?.code === 404) {
                setApiError(t('login.no_admin_with_kennitala'));
            } else {
                const msg = res?.results ? res.results[Object.keys(res.results)[0]] : '';
                setApiError(
                    res?.message || res?.error || t('login.error_occurred') + (msg ? '. ' + msg : '')
                );
            }
        } finally {
            setKennitalaLoading(false);
            setKennitalaVcode(null);
        }
    };

    const handleEmailSubmit = async () => {
        const trimmedEmail = email.trim().toLowerCase();
        let hasError = false;
        if (!EMAIL_RE.test(trimmedEmail)) {
            setEmailError(t('login.invalid_email'));
            hasError = true;
        } else {
            setEmailError('');
        }
        if (password.length < 6) {
            setPasswordError(t('login.password_min_6'));
            hasError = true;
        } else {
            setPasswordError('');
        }
        if (hasError) return;

        setApiError('');
        try {
            const data = await triggerEmail({ email: trimmedEmail, password });
            if (data?.message === 'Successfully login.' && data?.code === 200) {
                saveUserAndRedirect(parseUserData(data.results));
                return;
            }

            if (data?.results?.requires_passkey) {
                if (!passkeySupported) {
                    setApiError(t('login.passkey_required_unsupported'));
                    return;
                }

                setApiError(t('login.passkey_required'));
                setPasskeyLoading(true);
                try {
                    const { challenge_id: challengeId, publicKey } = data.results;
                    const credential = await getPasskey(publicKey);
                    const loginRes = await axios({
                        url: '/api/auth/webauthn/login',
                        method: 'POST',
                        data: { challenge_id: challengeId, credential },
                    });

                    if (
                        loginRes.data.message === 'Successfully login.' &&
                        loginRes.data.code === 200
                    ) {
                        setApiError('');
                        saveUserAndRedirect(parseUserData(loginRes.data.results));
                    } else {
                        setApiError(loginRes.data.message || t('login.passkey_error'));
                    }
                } catch (passkeyError) {
                    if (isUserCancelledError(passkeyError)) {
                        setApiError(t('login.passkey_required_cancelled'));
                    } else {
                        const msg =
                            passkeyError?.response?.data?.message ||
                            passkeyError?.message ||
                            t('login.passkey_error');
                        setApiError(msg);
                    }
                } finally {
                    setPasskeyLoading(false);
                }
            }
        } catch (error) {
            const msg =
                error?.response?.data?.message || error?.message || t('login.error_try_again');
            setApiError(msg);
        }
    };

    const handlePasskeySubmit = async () => {
        if (!passkeySupported) {
            setApiError(t('login.passkey_unsupported'));
            return;
        }

        setApiError('');
        setPasskeyLoading(true);

        try {
            const optionsRes = await axios({
                url: '/api/auth/webauthn/login/options',
                method: 'POST',
                data: {},
            });

            const { challenge_id: challengeId, publicKey } = optionsRes.data.results || {};
            if (!challengeId || !publicKey) {
                setApiError(t('login.passkey_error'));
                return;
            }

            const credential = await getPasskey(publicKey);

            const loginRes = await axios({
                url: '/api/auth/webauthn/login',
                method: 'POST',
                data: { challenge_id: challengeId, credential },
            });

            if (loginRes.data.message === 'Successfully login.' && loginRes.data.code === 200) {
                saveUserAndRedirect(parseUserData(loginRes.data.results));
            } else {
                setApiError(loginRes.data.message || t('login.passkey_error'));
            }
        } catch (error) {
            if (isUserCancelledError(error)) {
                setApiError(t('login.passkey_cancelled'));
            } else {
                const msg =
                    error?.response?.data?.message || error?.message || t('login.passkey_error');
                setApiError(msg);
            }
        } finally {
            setPasskeyLoading(false);
        }
    };

    const tabTestIds = {
        0: 'login-tab-sim',
        1: 'login-tab-app',
        2: 'login-tab-email',
        3: 'login-tab-passkey',
    };

    return (
        <div
            className={`${styles.page} ${styles.fadeIn} ${fadingOut ? styles.fadingOut : ''}`}
            data-testid="login-page"
        >
            <div className={styles.langSwitcherWrap}>
                <ThemeToggle />
                <LanguageSwitcher />
            </div>

            <div className={styles.brand} data-testid="login-brand">
                <h1 translate="no">Spara</h1>
                <p>{t('for_admins')}</p>
            </div>

            <div className={styles.card}>
                {/* Tab switcher */}
                <div
                    className={styles.tabBar}
                    style={{ '--tab-index': activeTab, '--tab-count': TAB_DEFS.length }}
                    data-testid="login-tab-bar"
                >
                    <span className={styles.tabIndicator} aria-hidden="true" />
                    {TAB_DEFS.map((tab) => (
                        <button
                            key={tab.id}
                            type="button"
                            data-testid={tabTestIds[tab.id]}
                            className={`${styles.tab} ${
                                activeTab === tab.id ? styles.tabActive : ''
                            }`}
                            onClick={() => handleTabChange(tab.id)}
                        >
                            {t(tab.labelKey)}
                        </button>
                    ))}
                </div>

                {/* Only mount the active tab — avoids inert/off-screen inputs
                    stealing focus (breaks Maestro web inputText). */}
                <div className={styles.panelViewport} ref={viewportRef}>
                    <div className={styles.panel} ref={panelRef} data-testid={`login-panel-${activeTab}`}>
                        {activeTab === 0 && (
                            <>
                                {apiError && (
                                    <p className={styles.errorMsg} data-testid="login-api-error">
                                        {apiError}
                                    </p>
                                )}
                                <div className={styles.fieldWrap}>
                                    <label className={styles.label}>{t('login.phone_label')}</label>
                                    <input
                                        data-testid="login-phone-input"
                                        className={`${styles.input}${
                                            phoneError ? ` ${styles.inputError}` : ''
                                        }`}
                                        type="tel"
                                        inputMode="numeric"
                                        placeholder={t('login.phone_placeholder')}
                                        value={phone}
                                        maxLength={7}
                                        autoComplete="off"
                                        onChange={(e) => {
                                            setPhone(e.target.value);
                                            setPhoneError('');
                                        }}
                                        onKeyDown={(e) => e.key === 'Enter' && handlePhoneSubmit()}
                                    />
                                    {phoneError && (
                                        <span
                                            className={styles.fieldError}
                                            data-testid="login-phone-error"
                                        >
                                            {phoneError}
                                        </span>
                                    )}
                                </div>
                                {phoneVcode && phoneLoading && (
                                    <div className={styles.vcodeBox} data-testid="login-phone-vcode">
                                        {t('login.security_code')}
                                        <b translate="no">{String(phoneVcode).padStart(4, '0')}</b>
                                    </div>
                                )}
                                <div className={styles.btnWrap}>
                                    <SparaButton
                                        data-testid="login-phone-submit"
                                        onClick={handlePhoneSubmit}
                                        loading={phoneLoading}
                                    >
                                        {t('login.continue')}
                                    </SparaButton>
                                </div>
                            </>
                        )}

                        {activeTab === 1 && (
                            <>
                                {apiError && (
                                    <p className={styles.errorMsg} data-testid="login-api-error">
                                        {apiError}
                                    </p>
                                )}
                                <div className={styles.fieldWrap}>
                                    <label className={styles.label}>
                                        {t('login.kennitala_label')}
                                    </label>
                                    <input
                                        data-testid="login-kennitala-input"
                                        className={`${styles.input}${
                                            kennitalaError ? ` ${styles.inputError}` : ''
                                        }`}
                                        type="text"
                                        inputMode="numeric"
                                        placeholder={t('login.kennitala_placeholder')}
                                        value={kennitala}
                                        maxLength={11}
                                        autoComplete="off"
                                        onChange={(e) => {
                                            setKennitala(e.target.value);
                                            setKennitalaError('');
                                        }}
                                        onKeyDown={(e) =>
                                            e.key === 'Enter' && handleKennitalaSubmit()
                                        }
                                    />
                                    {kennitalaError && (
                                        <span
                                            className={styles.fieldError}
                                            data-testid="login-kennitala-error"
                                        >
                                            {kennitalaError}
                                        </span>
                                    )}
                                </div>
                                {kennitalaVcode && kennitalaLoading && (
                                    <div
                                        className={styles.vcodeBox}
                                        data-testid="login-kennitala-vcode"
                                    >
                                        {t('login.security_code')}
                                        <b translate="no">
                                            {String(kennitalaVcode).padStart(4, '0')}
                                        </b>
                                    </div>
                                )}
                                <div className={styles.btnWrap}>
                                    <SparaButton
                                        data-testid="login-kennitala-submit"
                                        onClick={handleKennitalaSubmit}
                                        loading={kennitalaLoading}
                                    >
                                        {t('login.continue')}
                                    </SparaButton>
                                </div>
                            </>
                        )}

                        {activeTab === 2 && (
                            <>
                                {apiError && (
                                    <p className={styles.errorMsg} data-testid="login-api-error">
                                        {apiError}
                                    </p>
                                )}
                                <div className={styles.fieldWrap}>
                                    <label className={styles.label}>{t('login.email_label')}</label>
                                    <input
                                        data-testid="login-email-input"
                                        className={`${styles.input}${
                                            emailError ? ` ${styles.inputError}` : ''
                                        }`}
                                        type="email"
                                        placeholder="nafn@spara.is"
                                        value={email}
                                        autoComplete="username"
                                        onChange={(e) => {
                                            setEmail(e.target.value);
                                            setEmailError('');
                                        }}
                                        onKeyDown={(e) => e.key === 'Enter' && handleEmailSubmit()}
                                    />
                                    {emailError && (
                                        <span
                                            className={styles.fieldError}
                                            data-testid="login-email-error"
                                        >
                                            {emailError}
                                        </span>
                                    )}
                                </div>
                                <div className={styles.fieldWrap}>
                                    <label className={styles.label}>
                                        {t('login.password_label')}
                                    </label>
                                    <input
                                        data-testid="login-password-input"
                                        className={`${styles.input}${
                                            passwordError ? ` ${styles.inputError}` : ''
                                        }`}
                                        type="password"
                                        placeholder={t('login.password_placeholder')}
                                        value={password}
                                        autoComplete="current-password"
                                        onChange={(e) => {
                                            setPassword(e.target.value);
                                            setPasswordError('');
                                        }}
                                        onKeyDown={(e) => e.key === 'Enter' && handleEmailSubmit()}
                                    />
                                    {passwordError && (
                                        <span
                                            className={styles.fieldError}
                                            data-testid="login-password-error"
                                        >
                                            {passwordError}
                                        </span>
                                    )}
                                </div>
                                <div className={styles.btnWrap}>
                                    <SparaButton
                                        data-testid="login-email-submit"
                                        onClick={handleEmailSubmit}
                                        loading={emailLoading || passkeyLoading}
                                    >
                                        {t('login.sign_in')}
                                    </SparaButton>
                                </div>
                            </>
                        )}

                        {activeTab === 3 && (
                            <>
                                {apiError && (
                                    <p className={styles.errorMsg} data-testid="login-api-error">
                                        {apiError}
                                    </p>
                                )}
                                <p className={styles.passkeyHint}>
                                    {passkeySupported
                                        ? t('login.passkey_hint')
                                        : t('login.passkey_unsupported')}
                                </p>
                                <div className={styles.btnWrap}>
                                    <SparaButton
                                        data-testid="login-passkey-submit"
                                        onClick={handlePasskeySubmit}
                                        loading={passkeyLoading}
                                        disabled={!passkeySupported}
                                    >
                                        {t('login.sign_in_passkey')}
                                    </SparaButton>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </div>

            <p className={styles.footer}>© {new Date().getFullYear()} Spara</p>
        </div>
    );
}

export default withTranslation('', { withRef: true })(LoginPage);
